import { Editor } from 'slate';
import type { NodeEntry } from 'slate';

import type { RenderContentBlockOptions } from './content-block';
import { renderContentBlockFns } from './content-block';
import {
  isDynamicBlockElement,
  isDynamicBlockEntry,
  isNodeWithPopover,
  Queries,
} from '../../slate-api';
import type { RenderModelContentBlock, RenderModelDynamicBlock } from '../../slate-api';
import { assertNeverSafely, maxValueOf } from '../../util';
import { inactiveDynamicBlockColor } from '../colors';
import type { RenderContext } from '../engine/render-context';
import type { BaseRenderNodeOptions } from '../engine/rendering-data';
import { CANVAS_Z_INDICES } from '../z-indices';

const DYNAMIC_BLOCK_BAR_PADDING = 6;
const DYNAMIC_BLOCK_BAR_WIDTH = 2;

export interface RenderDynamicBlockOptions extends BaseRenderNodeOptions {
  dynamicBlocksImmediatelyAbove?: number;
}

export function renderDynamicBlock(
  renderContext: RenderContext,
  rMDynamicBlockEntry: NodeEntry<RenderModelDynamicBlock>,
  options: RenderDynamicBlockOptions
) {
  const { cMPath, editor, offsetX, rMSelIntersection } = renderContext;
  const canExpandAndCollapseDynamicBlocks = renderContext.withContext(
    'canExpandAndCollapseDynamicBlocks'
  );
  const [rMDynamicBlock, rMDynamicBlockPath] = rMDynamicBlockEntry;
  const { children, heightPx, widthPx } = rMDynamicBlock;
  const { dynamicBlocksImmediatelyAbove = 0 } = options;

  if (!cMPath) throw new Error('Expected cMPath');
  const dynamicBlockEntry = Editor.node(editor, cMPath);
  if (!isDynamicBlockEntry(dynamicBlockEntry)) throw new Error('Expected dynamic block');

  const isCollapsed = renderContext.withEditor(editor =>
    Queries.getIsCollapsedRMDynamicBlock(editor, { rMDynamicBlockEntry })
  );

  const isCollapsible =
    canExpandAndCollapseDynamicBlocks &&
    renderContext.withEditor(editor =>
      Queries.getIsCollapsibleRMDynamicBlock(editor, { rMDynamicBlockEntry })
    );

  if (isCollapsible)
    renderContext.renderReactComponent(
      -offsetX,
      0,
      'ToggleDynamicBlockButton',
      { clippable: false, key: `${rMDynamicBlockPath}-toggle-button` },
      { dynamicBlockEntry, isCollapsed }
    );

  if (isCollapsed) return;

  const isSelected = renderContext.withEditor(editor =>
    Queries.isLowestSelectedNodeOfType(editor, { match: isDynamicBlockElement, path: cMPath })
  );

  /**
   * Show the popover if the dynamic block is selected, the selection is collapsed inside this
   * dynamic block, and no other node with a popover is selected. Checking that the selection is
   * collapsed ensures that the dynamic block popover does not overlap with the selection ribbon.
   */
  const showPopover =
    isSelected &&
    !rMSelIntersection?.isExpanded &&
    rMSelIntersection?.isOriginalFocus &&
    renderContext.withEditor(editor =>
      Queries.isLowestSelectedNodeOfType(editor, { match: isNodeWithPopover, path: cMPath })
    );

  const dynamicBlockColors = renderContext.withContext('dynamicBlockColors');
  const activeColor = dynamicBlockColors.at(
    dynamicBlocksImmediatelyAbove % dynamicBlockColors.length
  );
  const color = dynamicBlockColors.length
    ? isSelected
      ? activeColor
      : inactiveDynamicBlockColor.toString()
    : null;

  const maxChildNegativeIndents = getMaxContentBlockNegativeMargins(children, {
    containerWidthPx: widthPx,
  });

  const parentBarsWidth = DYNAMIC_BLOCK_BAR_WIDTH * dynamicBlocksImmediatelyAbove;
  const leftBarOffset = DYNAMIC_BLOCK_BAR_PADDING + maxChildNegativeIndents.left - parentBarsWidth;
  const rightBarOffset =
    DYNAMIC_BLOCK_BAR_PADDING + maxChildNegativeIndents.right - parentBarsWidth;

  // Skip rendering children if no option is selected for the dynamic block
  if (rMDynamicBlock.triggerIndex !== 'not-set')
    renderContext.renderChildren(rMDynamicBlockEntry, renderContentBlockFns, {
      ...options,
      dynamicBlocksImmediatelyAbove: dynamicBlocksImmediatelyAbove + 1,
    } satisfies RenderContentBlockOptions);

  if (color) {
    renderContext.drawVerticalLine({
      clippable: false,
      color,
      endY: heightPx,
      startY: 0,
      thickness: DYNAMIC_BLOCK_BAR_WIDTH,
      x: -leftBarOffset,
      zIndex: CANVAS_Z_INDICES.DYNAMIC_BLOCK_BAR,
    });

    renderContext.drawVerticalLine({
      clippable: false,
      color,
      endY: heightPx,
      startY: 0,
      thickness: DYNAMIC_BLOCK_BAR_WIDTH,
      x: widthPx + rightBarOffset,
      zIndex: CANVAS_Z_INDICES.DYNAMIC_BLOCK_BAR,
    });
  }

  if (showPopover) {
    renderContext.renderReactComponent(
      0,
      0,
      'DynamicBlockPopoverAnchor',
      { clippable: false, key: `${rMDynamicBlockPath}-popover` },
      {
        dynamicBlockEntry,
        height: heightPx,
        width: widthPx,
      }
    );
  }
}

/**
 * Get the maximum negative left and right margin of a set of content blocks. Negative margin is
 * defined as the amount by which a block intrudes into the margins of its parent.
 */
function getMaxContentBlockNegativeMargins(
  contentBlocks: RenderModelContentBlock[],
  options: { containerWidthPx: number }
): { left: number; right: number } {
  const { containerWidthPx } = options;

  const negativeMargins = contentBlocks.flatMap(contentBlock => {
    switch (contentBlock.type) {
      case 'paragraph': {
        const negativeLeftIndent = -Queries.renderedParagraphLeftIndent(contentBlock, 'px');
        const negativeRightIndent = -Queries.renderedParagraphRightIndent(contentBlock, 'px');
        const negativeFirstLineIndent = -Queries.renderedParagraphFirstLineIndent(
          contentBlock,
          'px'
        );
        return {
          left: negativeLeftIndent + Math.max(0, negativeFirstLineIndent),
          right: negativeRightIndent,
        };
      }

      case 'table': {
        const indent = Queries.renderedTableIndent(contentBlock, 'px');
        return {
          left: -indent,
          right: contentBlock.widthPx + indent - containerWidthPx,
        };
      }

      case 'dynamic-block':
      case 'instructionField':
        return getMaxContentBlockNegativeMargins(contentBlock.children, { containerWidthPx });

      default:
        assertNeverSafely(contentBlock);
        return [];
    }
  });

  return {
    left: maxValueOf(negativeMargins, 'left', 0),
    right: maxValueOf(negativeMargins, 'right', 0),
  };
}
